import React, { ChangeEvent, ReactText, useContext, useEffect, useRef, useMemo, useState, useCallback } from 'react'
import { ErrorMessage as FormikErrorMessage, useField, useFormikContext } from 'formik'
import DatePicker from 'react-datepicker'
import Select from 'react-select'
import isEmpty from 'lodash/isEmpty'
import isDate from 'lodash/isDate'
import UserContext from '../../../UserContext'
import { ApiFetch } from '../Api'
import type { ReactDatePickerProps } from 'react-datepicker'
import type { OptionsType, Props as ReactSelectProps } from 'react-select'
import { groupBy } from 'lodash'
import { Accordion, AccordionContent, AccordionItem, AccordionTitle, Cell, Switch } from 'react-foundation'
import SimpleModal from '../SimpleModal'
import { SimpleModalContent, SimpleModalButton } from '../SimpleModalContent'

export const ErrorMessage: React.FC<{ name: string }> = ({ name }) => (
  <FormikErrorMessage name={name}>
    {msg => <span className="errorText">{msg}</span>}
  </FormikErrorMessage>
);
export const RequiredFlag: React.FC<{ required?: boolean }> = ({ required }) => required ? <span style={{ color: 'red' }}>*</span> : null

export type CommonProps = { name: string, title?: string, noLabel?: boolean, noError?: boolean, disabled?: boolean, required?: boolean }

export const FocusOnFirstError: React.FC = () => {
    const formik = useFormikContext()
    const [submitCount, setSubmitCount] = useState(formik.submitCount)

    useEffect(() => {
        if (!formik.isValid && formik.submitCount > submitCount) {
            if (global.window.document.getElementsByClassName('errorText').length) {
                const elements = global.window.document.getElementsByClassName('errorText')
                for (let i = 0; i < elements.length; i++) {
                    if ((elements[i] as HTMLElement).innerText !== '') {
                        const input = (elements[i] as HTMLElement).parentElement?.getElementsByTagName('input')
                        if (input && input.length > 0)
                            input[0].focus()
                        break
                    }
                }
            }
            setSubmitCount(formik.submitCount)
        }
    }, [formik.submitCount, submitCount, formik.isValid, formik.errors])

    return null
}

type InputFormGroupProps = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & CommonProps & { readOnlyDisplayMethod?: (value: unknown) => string, showErrorsOnlyOnSubmit?: boolean, submitCount?: number }

export const InputFormGroup: React.FC<InputFormGroupProps> = ({ name, title, noLabel, noError, readOnlyDisplayMethod, showErrorsOnlyOnSubmit, submitCount, ...props }) => {
    const [field, meta, helpers] = useField<unknown>(name);
    const formik = useFormikContext()

      const handleSetValue = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value;
        if (props.type === 'number') {
            helpers.setValue(v && !isNaN(Number(v)) ? Number(v) : undefined);
        } else {
            helpers.setValue(v);
        }
    };

    // Decide when to show error
    const shouldShowError = !noError && (
        showErrorsOnlyOnSubmit
        ? (submitCount ?? formik.submitCount) > 0
        : meta.touched || (submitCount ?? formik.submitCount) > 0
    );

    return (
        <>
            {!noLabel ? <label htmlFor={field.name}>{title || name} <RequiredFlag required={props.required} /></label> : null}
            <input
                {...field}
                {...props}
                required={props.required}
                name={field.name}
                type={props.type || 'text'}
                value={!props.disabled || !readOnlyDisplayMethod ? (meta.value as string ?? '') : readOnlyDisplayMethod((meta.value ?? ''))}
                onChange={handleSetValue} />
            {shouldShowError ? <ErrorMessage name={field.name} /> : null}
        </>
    )
}

type CheckBoxFormGroupProps = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & CommonProps & { readOnlyDisplayMethod?: (value: unknown) => string, onCheckedChange?: (checked: boolean) => void }

export const CheckBoxFormGroup: React.FC<CheckBoxFormGroupProps> = ({ name, title, noLabel, noError, readOnlyDisplayMethod, onCheckedChange, ...props }) => {
    const [field, { value }, { setValue }] = useField<boolean>(name)

    const handleSetValue = ({ target: { checked: v } }: React.ChangeEvent<HTMLInputElement>) => {
        setValue(v)
        if (onCheckedChange) onCheckedChange(v)
    }
        
    return (
        <>
            {!noLabel ?
                <label className="custom-checkbox-label">
                    <input
                        {...props}
                        className="custom-checkbox-input"
                        type="checkbox"
                        name={field.name || name}
                        value={field.name || name}
                        checked={value || false}
                        onChange={handleSetValue}
                    />
                    <span className="custom-checkbox"></span>
                    {title || name} <RequiredFlag required={props.required} />
                </label>
                : <input
                    {...props}
                    type="checkbox"
                    name={field.name || name}
                    value={field.name || name}
                    checked={value || false}
                    onChange={handleSetValue}
                />
            }
        </>
    )
}

type ShowIfFormGroupProps = { name: string, compareTo?: boolean}

export const ShowIfFormGroup: React.FC<ShowIfFormGroupProps> = ({ name, compareTo = true, ...props }) => {
    const [{ value }] = useField<boolean>(name)

    return (
        <>
            { (value || false) === compareTo ? props.children : null }
        </>
    )
}

type TextAreaGroupProps = React.DetailedHTMLProps<React.TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement> & CommonProps

export const TextAreaFormGroup: React.FC<TextAreaGroupProps> = ({ name, title, noLabel, noError, ...props }) => {
    const [field, { value }, { setValue }] = useField<string>(name)
    const [initialValue] = useState<string>(value)

    const handleOnInput = (e: ChangeEvent<HTMLTextAreaElement>) => {
        if (e.target.parentElement) e.target.parentElement.dataset.replicatedValue = e.target.value
        if (props.onInput) props.onInput(e)
    }

    return (
        <div className='textarea-dynamic-sizing'>
            {!noLabel ? <label htmlFor={field.name}>{title || name} <RequiredFlag required={props.required} /></label> : null}
            <div className='grow-wrap' data-replicated-value={initialValue}>
                <textarea {...props} name={field.name} value={value || ''} onChange={e => setValue(e.target.value)} onInput={handleOnInput} />
            </div>
            {!noError ? <ErrorMessage name={field.name} /> : null}
        </div>
    )
}

export const DatePickerFormGroup: React.FC<Omit<ReactDatePickerProps, 'onChange'> & CommonProps> = ({ name, title, noLabel, noError, ...props }) => {
    const [field, , { setValue }] = useField<Date>(name)

    useEffect(() => {
        if (!isDate(field.value) && !isEmpty(field.value)) setValue(new Date(field.value))
    }, [field, setValue, field.value])

    return (
        <>
            {!noLabel ? <label htmlFor={field.name}>{title || name} <RequiredFlag required={props.required} /></label> : null}
            <DatePicker
                {...props}
                dateFormat={['MM/dd/yyyy', 'MMddyyyy', 'yyyyMMdd']}
                selected={(field.value && new Date(field.value)) || null}
                onChange={v => setValue(v as Date)}
            />
            {!noError ? <ErrorMessage name={field.name} /> : null}
        </>
    )
}

export type OptionObjectType = { value: string, label?: string }
export type SelectFieldGroupProps = Omit<ReactSelectProps, 'options' | 'isDisabled'> & CommonProps & { options: (OptionObjectType | string)[], readableSeperator?: string }

export const SelectFieldGroup: React.FC<SelectFieldGroupProps> = ({ name, title, options, disabled, noLabel = false, noError, readableSeperator = '', ...props }) => {
    const [field, { value }, { setValue }] = useField<string>(name)

    const selectOptions: { value: string, label: string }[] = useMemo(() => typeof options[0] === 'string'
        ? (options as string[]).map(o => ({ value: o, label: o === '' ? 'Please make selection' : (readableSeperator === '' ? o : o.replace(/([a-z])([A-Z])/g, '$1' + readableSeperator + '$2')) }))
        : (options as OptionObjectType[]).map((o) => ({ value: o.value, label: o.label || o.value })), [options, readableSeperator])

    if (disabled) return <InputFormGroup name={name} title={title} noLabel={noLabel} disabled={disabled} />

    return (
        <>
            {!noLabel ? <label htmlFor={field.name}>{title || name} <RequiredFlag required={props.required} /></label> : null}
            <Select
                {...props}
                className='reactSelect'
                onChange={v => { if (v) setValue(v.value) }}
                value={value ? selectOptions.find(o => o.value === value) : null}
                options={selectOptions}
                isDisabled={disabled}
            />
            {!noError ? <ErrorMessage name={field.name} /> : null}
        </>
    )
}

const selectAllValue = '___selectall___'

type MultiSelectFieldGroupProps = SelectFieldGroupProps & { includeSelectAll?: boolean, includeAdditional?: string[], excludeFromAll?: string[], readOnlyMultiValueDisplay?: (values: unknown) => string, sort?: (values: string[]) => string[] }

export const MultiSelectFieldGroup: React.FC<MultiSelectFieldGroupProps> = ({ name, title, options, disabled, noLabel = false, noError, includeSelectAll, includeAdditional, excludeFromAll, readOnlyMultiValueDisplay, sort, ...props }) => {
    const [field, { value }, { setValue }] = useField<string[]>(name)

    const selectOptions: { value: string, label: string }[] = useMemo(() => {
        const mainOptions = typeof options[0] === 'string'
            ? (options as string[]).map(o => ({ value: o, label: o === '' ? 'Please make selection' : o }))
            : (options as OptionObjectType[]).map((o) => ({ value: o.value, label: o.label || o.value }))

        const additional = []

        //If select all is an option, and everything isn't already selcted, then include "Select All" as an option
        if (includeSelectAll && value?.length !== mainOptions.filter((o) => o.value).length)
            additional.push({ value: selectAllValue, label: 'Select All' })
        
        if (includeAdditional && includeAdditional.length > 0)
            for(let i = 0; i < includeAdditional.length; i++)
                additional.push({ value: includeAdditional[i], label: includeAdditional[i] })

        return [...additional, ...mainOptions]
    }, [options, value, includeSelectAll, includeAdditional])

    const handleChange = useCallback((v: OptionsType<OptionObjectType>) => {
        let newValues = v
        //If they selected "all" then set new values to everything.
        if (v.filter((o) => o.value === selectAllValue).length > 0)
            newValues = [...selectOptions.filter((o) => o.value && o.value !== selectAllValue && (!excludeFromAll || excludeFromAll.indexOf(o.value) < 0))]
        setValue(sort ? sort(newValues.map(o => o.value)) : newValues.map(o => o.value))
    }, [selectOptions, sort, setValue, excludeFromAll])

    if (disabled) return <InputFormGroup name={name} title={title} noLabel={noLabel} disabled={disabled} readOnlyDisplayMethod={readOnlyMultiValueDisplay} />

    return <>
        {!noLabel ? <label htmlFor={field.name}>{title || name} <RequiredFlag required={props.required} /></label> : null}
        <Select
            {...props}
            isMulti
            className='reactSelect'
            onChange={handleChange}
            value={value?.map(c => ({ value: c, label: c }))}
            options={selectOptions}
            isDisabled={disabled}
            styles={{ option: (styles, { data }) => ({ fontStyle: (data as OptionObjectType)?.value === selectAllValue ? 'italic' : '', ...styles }) }}
        />
        {!noError ? <ErrorMessage name={field.name} /> : null}
    </>
}

type LookupSelectFieldGroupProps = Omit<SelectFieldGroupProps, 'options'> & CommonProps & { lookupKey: string, includeBlank?: boolean }

export const LookupSelectFieldGroup: React.FC<LookupSelectFieldGroupProps> = ({ lookupKey, includeBlank, ...props }) => {
    const { cachedGet } = useContext(UserContext)
    const [list, setList] = useState<string[]>([])
    const { resetForm } = useFormikContext()

    const { CacheLookupsMinutes } = window.CS_CONFIG

    useEffect(() => {
        cachedGet('ApiLookup' + lookupKey, CacheLookupsMinutes, () => ApiFetch<string[]>('Lookup', { key: lookupKey }))
            .then((result) => {
                setList(result)
                if (resetForm) resetForm()
            })
            // eslint-disable-next-line no-console
            .catch(console.log)
    }, [lookupKey, resetForm, cachedGet, CacheLookupsMinutes])

    return props.isMulti
        ? <MultiSelectFieldGroup {...props} options={[...list]} />
        : <SelectFieldGroup {...props} options={[...(includeBlank ? [''] : []), ...list]} />
}

type LookupCheckboxFieldGroupProps = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & { lookupKey: string, tab?: string } & CommonProps

export const LookupCheckboxFieldGroup: React.FC<LookupCheckboxFieldGroupProps> = ({ name, title, lookupKey, noLabel, tab,...props }) => {
    const { cachedGet } = useContext(UserContext)
    const [list, setList] = useState<string[]>([])
    const { resetForm } = useFormikContext()
    const [field, { value: values }, { setValue: setValues }] = useField<string[] | undefined>(name)

    const { CacheLookupsMinutes } = window.CS_CONFIG
    useEffect(() => {
        cachedGet('ApiLookup' + lookupKey + '_' + tab, CacheLookupsMinutes, () => ApiFetch<string[]>('Lookup', { key: lookupKey, isOpenTab: (tab === 'Open') }))
            .then(
                (result) => {
                    setList(result)
                    if (resetForm)
                        resetForm()
                },
                () => { return } //Error handler
            )
    }, [lookupKey, resetForm, cachedGet, CacheLookupsMinutes])

    return (
        <>
            {!noLabel ? <label htmlFor={field.name}>{title || name} <RequiredFlag required={props.required} /></label> : null}
            {list.map((o, i) =>
                <label key={i}>
                    <input
                        {...props}
                        type="checkbox"
                        name={field.name}
                        value={o}
                        checked={values?.includes(o)}
                        onChange={e => setValues(e.target.checked
                            ? [...(values ?? []), e.target.value]
                            : values?.filter(v => v !== e.target.value)
                        )}
                    />
                    {o}
                </label>
            )}
        </>
    )
}


type RegionCheckboxFieldGroupProps = React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & { state?: string, options: Record<string, Record<string, string[]>>, selectAll?:boolean, handleChange?: (value:  EventTarget & HTMLInputElement) => void, onCountyChange?: (countyName: string, checked: boolean) => void, onZipChange?: (zipCode: string, checked: boolean) => void } & CommonProps

export const RegionCheckboxFieldGroup: React.FC<RegionCheckboxFieldGroupProps> = ({ state, name, title, options, noLabel, selectAll, handleChange, onCountyChange, onZipChange, ...props }) => {

    const [field, { value: values }, { setValue: setValues }] = useField<Record<string, Record<string, string[]>> | undefined>(name)
    const { setFieldValue } = useFormikContext();
    const [showAccordion, setShowAccordion] = useState<string[]>([])
    if (state !== undefined) options = Object.fromEntries(Object.entries(options).filter(([key]) => key.includes(`:${state}`)));

    const normalizeZipCode = (zip: string) => {
        const normalizedZip = zip.toString().trim();
        if (!normalizedZip) return '';
        if (/^\d{5}(-\d{4})?$/.test(normalizedZip)) return normalizedZip.slice(0, 5);
        if (/^\d{1,5}$/.test(normalizedZip)) return normalizedZip;
        return normalizedZip;
    };

    const uniqueZipCodes = (zips: string[]) => Array.from(
        new Set(
            (zips || [])
                .map((zip) => normalizeZipCode(zip))
                .filter((zip) => zip !== '')
        )
    );
    const getRegionValues = (regionName: string) => {
        const region = values?.[regionName];
        return region && typeof region === 'object' ? region : {} as Record<string, string[]>;
    }

    const getCountySelectedZips = (regionName: string, countyName: string) => {
        const selected = values?.[regionName]?.[countyName];
        return Array.isArray(selected) ? selected : [] as string[];
    }

    const cloneServiceArea = () => {
        return Object.entries(values || {}).reduce((serviceAreaClone, [regionName, counties]) => {
            serviceAreaClone[regionName] = Object.entries(counties || {}).reduce((countyClone, [countyName, zips]) => {
                countyClone[countyName] = Array.isArray(zips) ? uniqueZipCodes(zips) : [];
                return countyClone;
            }, {} as Record<string, string[]>);
            return serviceAreaClone;
        }, {} as Record<string, Record<string, string[]>>);
    }

    const updateServiceArea = (mutator: (next: Record<string, Record<string, string[]>>) => void) => {
        const next = cloneServiceArea();
        mutator(next);
        setFieldValue(field.name, next);
    }

    const toggleZipAcrossSections = (zipCode: string, isChecked: boolean) => {
        const next = cloneServiceArea();

        if (isChecked) {
            Object.keys(options).forEach((regionKey) => {
                Object.keys(options[regionKey] || {}).forEach((countyKey) => {
                    const countyOptions = Array.isArray(options[regionKey][countyKey]) ? options[regionKey][countyKey] : [];
                    if (!countyOptions.includes(zipCode)) return;

                    if (!next[regionKey]) next[regionKey] = {};
                    if (!Array.isArray(next[regionKey][countyKey])) next[regionKey][countyKey] = [];
                    if (!next[regionKey][countyKey].includes(zipCode)) {
                        next[regionKey][countyKey] = uniqueZipCodes([zipCode, ...next[regionKey][countyKey]]);
                    }
                });
            });
            setFieldValue(field.name, next);
            return;
        }

        Object.keys(next).forEach((regionKey) => {
            Object.keys(next[regionKey]).forEach((countyKey) => {
                const countyZips = Array.isArray(next[regionKey][countyKey]) ? next[regionKey][countyKey] : [];
                next[regionKey][countyKey] = countyZips.filter((value) => value !== zipCode);
            });
        });

        setFieldValue(field.name, next);
    }
    
    const getRegionZipCodeList = (values: Record<string, Record<string, string[]>>, regionName: string) => {
        const zipCodes = Object.keys(values[regionName]).reduce((acc, countyName) => {
            const zips = options[regionName] && options[regionName][countyName];
            if (!zips) return acc;

            return acc.concat(uniqueZipCodes(zips));
        }, [] as string[]);

        return uniqueZipCodes(zipCodes).sort();
    }

    const isZipCodeSelectedInRegion = (regionName: string, zipCode: string) => {
        const selectedCounties = Object.keys(getRegionValues(regionName));
        return selectedCounties.some((countyName) => getCountySelectedZips(regionName, countyName).includes(zipCode));
    }

    const isSelectAllZipCodesChecked = (region: string) => {
        if (values === undefined || values[region] === undefined) return false;
        const counties = Object.keys(values[region]);
        const selected = counties.reduce((count, county) => count + getCountySelectedZips(region, county).length, 0);
        //const possible = counties.reduce((count, county) => count + options[region][county].length, 0);
        return selected > 0;
    }
    return (
            <Accordion className='service-area-regions-accordion'>
            {Object.keys(options).map((regionName: string) =>
                <AccordionItem key={regionName}>
                    <AccordionTitle onClick= {() =>{setShowAccordion(showAccordion.includes(regionName) ? showAccordion.filter(r => r !== regionName) : [regionName, ...showAccordion])}}>
                        <span className='title-container service-area-region-title-container'>
                            <h5 className='title'>
                                {regionName.split(':')[0] + ' Region'}
                            </h5>
                            <i className={`fa-regular service-area-caret fa-2xl ${showAccordion.includes(regionName) ? 'fa-caret-up' : 'fa-caret-down'}`}></i>
                         </span>
                       
                    </AccordionTitle>
                    <AccordionContent isActive={showAccordion.includes(regionName)}>
                        <section className='provider-preferences-counties-section'>
                            <div className='service-area-section-header'>
                                <h6 className="title">Counties</h6>
                                {values !== undefined ? <label className='select-all service-area-select-all'>
                                    <input
                                        type="checkbox"
                                        checked={values !== undefined && Object.keys(getRegionValues(regionName)).length > 0}
                                        onChange={e => {
                                            if (e.target.checked) {
                                                updateServiceArea((next) => {
                                                    next[regionName] = Object.entries(options[regionName] || {}).reduce((regionClone, [countyKey, zips]) => {
                                                        regionClone[countyKey] = uniqueZipCodes(zips);
                                                        return regionClone;
                                                    }, {} as Record<string, string[]>);
                                                });
                                            } else {
                                                updateServiceArea((next) => {
                                                    next[regionName] = {};
                                                });
                                            }
                                        }}
                                    />
                                    {Object.keys(getRegionValues(regionName)).length > 0 ? 'Deselect All' : 'Select All'}
                                </label> : null}
                            </div>
                            <div className='service-area-item-grid service-area-county-grid'>
                            {values !== undefined ? Object.keys(options[regionName]).map((countyName: string) => 
                                <label  className='col-item service-area-county-item' key={countyName}>
                                    <input
                                        {...props}
                                        type="checkbox"
                                        name={field.name}
                                        value={countyName}
                                        checked={values !== undefined && countyName in getRegionValues(regionName)}
                                        onChange={e => {
                                            if (e.target.checked) {
                                                updateServiceArea((next) => {
                                                    if (!next[regionName]) next[regionName] = {};
                                                    next[regionName][countyName] = uniqueZipCodes(options[regionName]?.[countyName] || []);
                                                });
                                            } else {
                                                updateServiceArea((next) => {
                                                    if (!next[regionName]) return;
                                                    let clone = {...next[regionName]};
                                                    delete clone[countyName];
                                                    next[regionName] = clone;
                                                });
                                            }
                                            onCountyChange?.(countyName, e.target.checked)
                                        }}
                                    />
                                    {countyName.split(':')[0]}
                                </label>
                            ): null}
                            </div>
                        <div className="preferences-clearfix"></div>
                        </section>

                    {values !== undefined && values[regionName] !== undefined && Object.keys(values[regionName]).length !== 0 ? 
                        <section className="service-area-zip-code-section">
                            <div className='service-area-section-header'>
                                <h6 className='title'>Specific Zip Codes</h6>
                                <label className='select-all service-area-select-all'>
                                    <input
                                        type="checkbox"
                                        checked={values !== undefined && isSelectAllZipCodesChecked(regionName)}
                                        onChange={e => {
                                            const counties = Object.keys(getRegionValues(regionName));
                                            if (e.target.checked) {
                                                const regionObj = counties.reduce((regionObj: Record<string, string[]>, county) => {
                                                    regionObj[county] = uniqueZipCodes(options[regionName]?.[county] || [])
                                                    return regionObj;
                                                }, {})
                                                updateServiceArea((next) => {
                                                    next[regionName] = regionObj;
                                                });
                                            } else {
                                                const regionObj = counties.reduce((regionObj: Record<string, string[]>, county) => {
                                                    regionObj[county] = [];
                                                    return regionObj;
                                                }, {})
                                                updateServiceArea((next) => {
                                                    next[regionName] = regionObj;
                                                });
                                            }
                                        }}
                                    />
                                    { isSelectAllZipCodesChecked(regionName) ? 'Deselect All' : 'Select All' }
                                </label>
                            </div>
                            <div className='service-area-item-grid service-area-zip-grid'>
                                {getRegionZipCodeList(values, regionName).map((zipCode) =>
                                    <label className='col-item service-area-zip-item' key={`${regionName}-${zipCode}`}>
                                        <input
                                            {...props}
                                            type="checkbox"
                                            name={field.name}
                                            value={zipCode}
                                            checked={isZipCodeSelectedInRegion(regionName, zipCode)}
                                            onChange={e => {
                                                toggleZipAcrossSections(zipCode, e.target.checked)
                                                onZipChange?.(zipCode, e.target.checked)
                                            }}
                                        />
                                        {zipCode}
                                    </label>
                                )}
                            </div>
                            <p className='service-area-zip-sync-note'>ZIP codes shared by multiple counties are shown once and synced across all matching counties when selected or deselected.</p>
                    </section>
                    : null}
                    </AccordionContent>

                </AccordionItem>
            )}

            {values !== undefined ? (
                <h6 className='service-area-selected-count'>{`Selected: ${Array.from(
                    new Set(
                        Object.values(values)
                            .flatMap((region) => Object.entries(region).filter(([key, value]) => state === undefined || state === key.split(':')[1]).map(([key, value]) => value))
                            .flatMap((county) => county)
                            .map((zip) => zip.toString().slice(0, 5))
                    )
                ).length} zip codes`}</h6>
            ) : null}
            </Accordion>
    )
}

type NearbyCountySelectFieldGroupProps = Omit<SelectFieldGroupProps, 'options'> & CommonProps & { stateAbbr: string, county?: string, includeBlank?: boolean }

export const NearbyCountySelectFieldGroup: React.FC<NearbyCountySelectFieldGroupProps> = ({ stateAbbr, county, includeBlank, ...props }) => {
    const [counties, setCounties] = useState<string[]>([])
    const { cachedGet } = useContext(UserContext)
    const { CacheLookupsMinutes } = window.CS_CONFIG

    useEffect(() => {
        if (!county) return setCounties([])

        cachedGet(`ApiAdjacentCountiesLookup_${stateAbbr}_${county}`, CacheLookupsMinutes, () => ApiFetch<string[]>('County/Adjacent', { stateABbr: stateAbbr, county: county }))
            .then((result) => setCounties(result))
            // eslint-disable-next-line no-console
            .catch(console.log)
    }, [cachedGet, CacheLookupsMinutes, county, stateAbbr])

    if (!county) return null

    return <SelectFieldGroup {...props} options={[...(includeBlank ? [''] : []), ...counties]} />
}
